import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SessionUser } from '@appcycle/contracts';
import { getSession, logout, openLogin } from './api';

interface SessionState {
  user: SessionUser | null;
  loading: boolean;
  signIn: () => void;
  signOut: () => Promise<void>;
  isOperator: boolean;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getSession().then((session) => setUser(session.user)).catch(() => setUser(null)).finally(() => setLoading(false));
  }, []);
  const value = useMemo<SessionState>(() => ({
    user,
    loading,
    signIn: () => openLogin(),
    signOut: logout,
    isOperator: Boolean(user?.roles.some((role) => ['admin', 'maintainer', 'editor'].includes(role))),
  }), [loading, user]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
}
